/**
 * Explorar mesas: abas, consulta e catálogo de características (docs/spec/explore-rooms.md).
 */
import { z } from 'zod';
import { deskFileRefId } from './deskCloud.js';
import { GameRulesSchema } from './domain.js';
import { ROOM_ABOUT_MAX, ROOM_PRICE_MAX, RoomFrequencySchema, RoomLanguageSchema, RoomVisualsSchema } from './roomDetails.js';

/** Abas do Explorar, na ordem da navegação vertical do cliente de referência. */
export const EXPLORE_TABS = ['online', 'featured', 'discover', 'recent', 'favorites', 'mine', 'search'] as const;
export const ExploreTabSchema = z.enum(EXPLORE_TABS);
export type ExploreTab = z.infer<typeof ExploreTabSchema>;

export const ExploreSortSchema = z.enum(['recommendation', 'onlinePlayersQty']);
export type ExploreSort = z.infer<typeof ExploreSortSchema>;

const flag = z
  .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
  .transform((value) => value === true || value === 'true' || value === '1')
  .default(false);
const tagList = z
  .union([z.array(z.string()), z.string()])
  .transform((value) => (Array.isArray(value) ? value : value.split(',')).map((tag) => tag.trim().toLowerCase()).filter(Boolean).slice(0, 24))
  .default([]);

/**
 * O sistema da mesa é texto livre: "D&D 5e" e "d&d  5E" são o mesmo sistema para filtrar e contar.
 */
export function systemKey(system: string): string {
  return system.trim().replace(/\s+/g, ' ').toLocaleLowerCase();
}

/** Os sistemas escolhidos vão juntos num parâmetro só, separados por `|` (o nome pode ter vírgula). */
export const SYSTEMS_SEPARATOR = '|';
const systemList = z
  .union([z.array(z.string()), z.string()])
  .transform((value) => (Array.isArray(value) ? value : value.split(SYSTEMS_SEPARATOR)).map(systemKey).filter(Boolean).slice(0, 24))
  .default([]);

/** Parâmetros de uma página do Explorar: os filtros valem em qualquer aba. */
export const ExploreQuerySchema = z.object({
  sort: ExploreSortSchema.default('recommendation'),
  limit: z.coerce.number().int().min(1).max(200).default(60),
  query: z.string().max(120).default(''),
  withoutPassword: flag,
  withPlayersOnline: flag,
  withGMOnline: flag,
  /** o dono marcou "há vagas" */
  withVacancies: flag,
  /** sem as mesas +18 (Configurações → Geral), menos as que a pessoa administra */
  withoutAdult: flag,
  requiredTags: tagList,
  notTags: tagList,
  /** um destes sistemas (comparados por `systemKey`) */
  systems: systemList,
  /** como é a mesa (roomDetails.ts): uma destas línguas, frequências ou visuais */
  languages: tagList,
  frequencies: tagList,
  visuals: tagList,
  /** só as gratuitas ou só as pagas; ausente = qualquer uma */
  pay: z.enum(['free', 'paid']).optional(),
});
export type ExploreQuery = z.output<typeof ExploreQuerySchema>;
export type ExploreQueryInput = z.input<typeof ExploreQuerySchema>;

/**
 * O que acompanha a lista de uma aba: quantas mesas cada aba teria com os mesmos filtros, e os
 * sistemas e as línguas da aba (cada um com os outros filtros, sem o seu), do mais usado para o menos.
 */
export type ExploreFacets = {
  counts: Record<ExploreTab, number>;
  systems: { name: string; count: number }[];
  /** as línguas da aba (com os outros filtros, sem o de língua), da mais usada para a menos */
  languages: { code: string; count: number }[];
};

/**
 * Características de mesa do RPG Desk (no original o catálogo vem do servidor).
 * Os nomes mostrados ficam no i18n do cliente (`roomTag.<id>`); salas antigas
 * podem ter tags livres, que aparecem como foram escritas.
 */
export const ROOM_TAGS = [
  'campaign',
  'one-shot',
  'beginners',
  'veterans',
  'fantasy',
  'horror',
  'sci-fi',
  'modern',
  'historical',
  'investigation',
  'comedy',
  'intrigue',
  'combat',
  'roleplay',
  'voice',
  'text-only',
  'homebrew',
  'mature',
] as const;
export type RoomTag = (typeof ROOM_TAGS)[number];

/**
 * O nome de cada característica em português e em inglês (os mesmos de `roomTag.<id>` no
 * cliente): a busca do Explorar acha a mesa pelo nome da característica nas duas línguas.
 */
export const ROOM_TAG_NAMES: Readonly<Record<RoomTag, readonly [pt: string, en: string]>> = {
  campaign: ['Campanha', 'Campaign'],
  'one-shot': ['Aventura de uma sessão', 'One-shot'],
  beginners: ['Bom para iniciantes', 'Beginner friendly'],
  veterans: ['Para veteranos', 'For veterans'],
  fantasy: ['Fantasia', 'Fantasy'],
  horror: ['Terror', 'Horror'],
  'sci-fi': ['Ficção científica', 'Science fiction'],
  modern: ['Contemporâneo', 'Modern day'],
  historical: ['Histórico', 'Historical'],
  investigation: ['Investigação', 'Investigation'],
  comedy: ['Comédia', 'Comedy'],
  intrigue: ['Intriga', 'Intrigue'],
  combat: ['Muito combate', 'Combat heavy'],
  roleplay: ['Muita interpretação', 'Roleplay heavy'],
  voice: ['Usa voz', 'Uses voice'],
  'text-only': ['Só texto', 'Text only'],
  homebrew: ['Regras da casa', 'House rules'],
  mature: ['Temas adultos', 'Mature themes'],
};

/** Nome mínimo de mesa no popup de criação. */
export const ROOM_NAME_MIN = 5;

/** Quem lê o histórico do chat ao entrar (logReadAccessPermissionModel no original). */
export const LogAccessSchema = z.enum(['everyone', 'receipt']);
export type LogAccess = z.infer<typeof LogAccessSchema>;

/**
 * A capa e a marca da mesa vindas do Desk Cloud (docs/spec/desk-cloud.md, "Capa e marca da mesa"): os
 * tipos que o navegador mostra, o maior arquivo e o maior lado depois da redução no envio.
 */
export const ROOM_IMAGE_LIMITS = {
  types: ['image/png', 'image/jpeg', 'image/webp', 'image/gif'],
  maxBytes: 5 * 1024 * 1024,
  coverSide: 1600,
  logoSide: 512,
} as const;

// um endereço da web, um arquivo do Desk Cloud (`rpgdesk-file:<id>`) ou o `/files/` de antes, que fica
// aceito só para as mesas que ainda o guardam conseguirem salvar
const imageUrl = z
  .string()
  .trim()
  .max(2000)
  .refine((url) => url === '' || /^https?:\/\//i.test(url) || url.startsWith('/files/') || deskFileRefId(url) !== null, { message: 'endereço de imagem inválido' })
  .transform((url) => (url === '' ? null : url))
  .nullable();

/**
 * Edição do perfil e das permissões da mesa (TfrmRoomProfileStateForm e
 * TfrmRoomPermissionsStateForm). Campo ausente = não muda. `password: null` tira a senha. O `price`
 * some no servidor quando a mesa fica sem `payToPlay`.
 */
export const RoomSettingsPatchSchema = z
  .object({
    name: z.string().trim().min(ROOM_NAME_MIN).max(60),
    rpgSystem: z.string().trim().max(60),
    rpgSystemComplement: z.string().trim().max(60),
    tags: z.array(z.string().trim().toLowerCase().min(1).max(24)).max(12),
    welcomeMessage: z.string().trim().max(500),
    statusMessage: z.string().trim().max(120),
    coverUrl: imageUrl,
    logoUrl: imageUrl,
    haveVacancies: z.boolean(),
    restrictedUnder18: z.boolean(),
    isPrivate: z.boolean(),
    password: z.string().min(1).max(120).nullable(),
    logAccess: LogAccessSchema,
    gameRules: GameRulesSchema,
    language: RoomLanguageSchema,
    frequency: RoomFrequencySchema,
    visuals: RoomVisualsSchema,
    payToPlay: z.boolean(),
    price: z.string().trim().max(ROOM_PRICE_MAX),
    about: z.string().trim().max(ROOM_ABOUT_MAX),
  })
  .partial()
  .refine((patch) => Object.keys(patch).length > 0, { message: 'nada para mudar' });
export type RoomSettingsPatch = z.output<typeof RoomSettingsPatchSchema>;
export type RoomSettingsPatchInput = z.input<typeof RoomSettingsPatchSchema>;
